import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import Seo from "../components/Seo";

const StarPicker = ({ value, onChange }) => (
  <div className="flex gap-2" data-testid="review-rating-picker">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        data-testid={`review-star-${n}`}
        aria-label={`${n} star${n === 1 ? "" : "s"}`}
        onClick={() => onChange(n)}
        className={`w-12 h-12 flex items-center justify-center border transition-all ${
          n <= value
            ? "border-gold text-gold bg-gold/10"
            : "border-white/15 text-white/30 hover:text-white"
        }`}
      >
        <Star size={22} fill={n <= value ? "currentColor" : "none"} />
      </button>
    ))}
  </div>
);

const ReviewSubmit = () => {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .get(`/reviews/decode?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setMeta(data))
      .catch((err) =>
        setLoadErr(err.response?.data?.detail || "This review link is invalid or expired.")
      );
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 10) {
      toast.error("Please write a few more words about your experience (at least 10 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews/submit", {
        token,
        rating,
        title: title.trim() || undefined,
        body: body.trim(),
        display_name: displayName.trim() || undefined,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your review.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="review-submit-page" className="min-h-screen bg-noir text-ivory py-20">
      <Seo title="Share your review · OPIKJEWELS" noindex />
      <div className="max-w-2xl mx-auto px-6">
        <Link to="/" className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold hover:text-ivory">
          ← Opik Jewels
        </Link>

        {loadErr && (
          <div className="mt-12 border border-red-400/40 bg-red-400/10 p-8 text-red-200">
            {loadErr}
          </div>
        )}

        {!loadErr && !meta && (
          <div className="mt-12 text-white/40">Loading…</div>
        )}

        {meta && done && (
          <div className="mt-16 border border-gold/40 bg-gold/5 p-10 text-center">
            <div className="w-14 h-14 rounded-full bg-gold/20 text-gold flex items-center justify-center mx-auto mb-5">
              <Check size={28} />
            </div>
            <h1 className="font-heading text-3xl text-ivory">Thank you.</h1>
            <p className="text-white/60 mt-3 max-w-md mx-auto">
              Your review has been received and will appear on the product page once a senior advisor has verified it. Future custodians thank you.
            </p>
            <Link
              to="/collection"
              className="inline-block mt-8 font-accent text-[10px] tracking-[0.28em] uppercase border border-gold text-gold px-6 py-3 hover:bg-gold hover:text-black transition-colors"
            >
              Return to collection
            </Link>
          </div>
        )}

        {meta && !done && (
          <>
            <div className="mt-12 mb-8 flex gap-5 items-center">
              {meta.gemstone_image && (
                <div className="w-20 h-20 overflow-hidden bg-black/40">
                  <img
                    src={meta.gemstone_image}
                    alt={meta.gemstone_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div>
                <div className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">
                  Sharing your review of
                </div>
                <div className="font-heading text-2xl text-ivory mt-1">
                  {meta.gemstone_name}
                </div>
                {meta.order_reference && (
                  <div className="text-xs text-white/40 mt-1 font-mono">
                    Order {meta.order_reference}
                  </div>
                )}
              </div>
            </div>

            <form onSubmit={submit} className="space-y-6 bg-onyx border border-white/10 p-8">
              <div>
                <label className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">
                  Your rating
                </label>
                <div className="mt-2.5">
                  <StarPicker value={rating} onChange={setRating} />
                </div>
              </div>

              <div>
                <label className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">
                  Title (optional)
                </label>
                <input
                  data-testid="review-title"
                  type="text"
                  maxLength={160}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="A short headline"
                  className="mt-2 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none"
                />
              </div>

              <div>
                <label className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">
                  Your review
                </label>
                <textarea
                  data-testid="review-body"
                  required
                  rows={5}
                  maxLength={2000}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="A few sentences about your gemstone and your experience…"
                  className="mt-2 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none resize-y"
                />
                <div className="text-xs text-white/40 mt-1.5">{body.length} / 2000</div>
              </div>

              <div>
                <label className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">
                  Display name (optional)
                </label>
                <input
                  data-testid="review-display-name"
                  type="text"
                  maxLength={80}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="How you'd like to appear publicly (e.g. 'James L.')"
                  className="mt-2 w-full bg-black border border-white/15 text-ivory px-3 py-2.5 text-sm focus:border-gold outline-none"
                />
                <div className="text-xs text-white/40 mt-1.5">
                  Leave blank to be shown as "Verified Custodian".
                </div>
              </div>

              <button
                type="submit"
                data-testid="review-submit"
                disabled={submitting}
                className="w-full bg-gold text-black font-accent uppercase tracking-[0.24em] text-[10px] py-3.5 hover:bg-ivory transition-colors disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit review"}
              </button>

              <p className="text-xs text-white/40 italic text-center">
                Your email is never shown publicly. Each review is checked by a senior advisor before going live.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ReviewSubmit;
